import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import type { SessionInfo } from "@analytics/shared";
import { api } from "./api.js";

export type ViewId = "overview" | "leaddist" | "users";

interface AppState {
  session: SessionInfo | null;
  setSession: (s: SessionInfo | null) => void;
  view: ViewId;
  setView: (v: ViewId) => void;
  /** dashboards this session may see (from the server; code access list) */
  canSee: (d: "lead-distribution") => boolean;
  /** re-fetch /session (after impersonation start/stop or a role/scope edit) */
  reloadSession: () => Promise<void>;
}

const Ctx = createContext<AppState | null>(null);

function urlParam(name: string): string | null {
  if (typeof window === "undefined") return null;
  try { return new URLSearchParams(window.location.search).get(name); } catch { return null; }
}
function syncUrl(view: ViewId) {
  if (typeof window === "undefined") return;
  try {
    const p = new URLSearchParams(window.location.search);
    p.set("view", view);
    window.history.replaceState(null, "", `${window.location.pathname}?${p.toString()}`);
  } catch { /* ignore */ }
}

export function AppProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<SessionInfo | null>(null);
  const [view, setViewRaw] = useState<ViewId>((urlParam("view") as ViewId) || "overview");

  const setView = useCallback((v: ViewId) => { setViewRaw(v); syncUrl(v); }, []);
  const canSee = useCallback(
    (d: "lead-distribution") => (session?.dashboards ?? []).includes(d),
    [session],
  );
  const reloadSession = useCallback(async () => {
    try { setSession(await api.session()); } catch { /* ignore */ }
  }, []);

  useEffect(() => { syncUrl(view); /* eslint-disable-next-line */ }, []);

  const value: AppState = { session, setSession, view, setView, canSee, reloadSession };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useApp() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useApp outside provider");
  return v;
}

/** Generic async data hook; refetches when any dep changes. */
export function useAsync<T>(fn: () => Promise<T>, deps: unknown[]): {
  data: T | undefined; loading: boolean; error: Error | null; reload: () => void;
} {
  const [data, setData] = useState<T>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [nonce, setNonce] = useState(0);
  const depsKey = JSON.stringify(deps);
  const prevDeps = useRef<string | null>(null);

  useEffect(() => {
    let alive = true;
    // When the inputs change (a new filter / page) — as opposed to a manual reload —
    // drop the previous result, so a widget never renders a prior filter's data under
    // the new filter while this (possibly slower) refetch is still in flight. Endpoints
    // resolve at different speeds; without this the cards could update to the new filter
    // while a slower table still showed the old one (the leaddist "207" stale-render).
    if (prevDeps.current !== null && prevDeps.current !== depsKey) {
      setData(undefined);
    }
    prevDeps.current = depsKey;
    setLoading(true);
    setError(null);
    fn()
      .then((d) => alive && (setData(d), setLoading(false)))
      .catch((e) => alive && (setError(e as Error), setLoading(false)));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  return { data, loading, error, reload: () => setNonce((n) => n + 1) };
}

export { api };
